import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {SelectDropdown} from 'common/components/select';
import {KEY_VALUES} from 'common/util/dom';

import '../tag';
import css from './index.styl?inline';
import {template} from './template';
import {TagItem} from './tag-item';
import {TagInputJsxProps, TagInputState, TagInputTheme} from './types';

const MIN_INPUT_WIDTH = `12px`;

export class TagInput extends Component<TagInputState, TagInputJsxProps['attrs']> {
  static get attrsSchema(): AttrsSchema<TagInputJsxProps['attrs']> {
    return {
      error: `boolean`,
      'min-tags': {type: `number`, default: 1},
      'search-text': {type: `string`, default: `Search`},
      'show-search-icon': `boolean`,
      tags: {
        type: `json`,
        default: [],
        description: `interface: TagItem[], with a label and optional isDisabled
        and attrs params (attrs gets passed down to the mp-tag attrs)`,
      },
      placeholder: `string`,
      'disable-input': `boolean`,
      'mp-select-attrs': `json`,
      theme: {type: `string`, default: TagInputTheme.Default},
    };
  }

  get config() {
    return {
      defaultState: {
        isInputFocused: false,
        inputText: ``,
        inputWidth: MIN_INPUT_WIDTH,
        tags: new Set<TagItem>(),
      },
      css,
      template,
      helpers: {
        processMpSelectAttrs: () => {
          const mpSelectAttrs = this.attr(`mp-select-attrs`);
          const mpSelectAttributeSchema = SelectDropdown.attrsSchema;
          const jsonAttrsNames = Object.entries(mpSelectAttributeSchema)
            .filter(([, value]) => typeof value !== `string` && value.type === `json`)
            .map(([key]) => key);

          const processedAttrs = {
            elref: `mpTagInputMpSelect`,
          };
          for (const key in mpSelectAttrs) {
            processedAttrs[key] = jsonAttrsNames.includes(key)
              ? JSON.stringify(mpSelectAttrs[key])
              : mpSelectAttrs[key];
          }
          return processedAttrs;
        },
        onSelectMenuChange: (ev) => {
          this.update({isInputFocused: ev.detail.isOpen});
        },
        onSelectChange: (ev) => {
          this.dispatchEvent(new CustomEvent(`optionSelected`, {detail: ev.detail}));
        },
        onFocusChange: (ev) => {
          this.update({isInputFocused: ev.type === `focus`});
        },
        canRemoveTag: (tag) =>
          this.attr(`theme`) === TagInputTheme.Breadcrumbs ||
          (this.state.tags.size > this.attr(`min-tags`) && !tag.isDisabled),
        setInputText: (ev) => {
          this.update({inputText: ev.target.value});
          this._resizeInput();
          this.dispatchEvent(new CustomEvent(`change`, {detail: {value: this.state.inputText, action: `text`}}));
        },
        removeTag: (tagName) => () => this._removeTag(tagName),
        focusInput: () => this.focus(),
        handleKeyDown: (ev) => {
          // ev.keyCode is deprecated
          if (ev.code === KEY_VALUES.BACKSPACE && this.state.inputText === ``) {
            const tagList = Array.from(this.state.tags);
            for (let i = tagList.length - 1; i >= 0; i--) {
              if (this.config.helpers.canRemoveTag(tagList[i])) {
                this._removeTag(tagList[i]);
                break;
              }
            }
          }
        },
        getPlaceholder: () => {
          if (this.state.tags.size) {
            return ``;
          }
          return this.attr(`placeholder`) || ``;
        },
      },
      useShadowDom: true,
    };
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === `tags`) {
      this.update({tags: new Set(this.attr(`tags`)), inputText: ``});
    }
  }

  focus() {
    const searchInput: HTMLInputElement = this.el.querySelector(`.mp-tag-input-search-input`);
    if (searchInput) {
      searchInput.focus();
    }
  }

  set value(value: string) {
    this.update({inputText: value});
  }

  _resizeInput() {
    // make the search input width dynamic
    const searchInput: HTMLInputElement = this.el.querySelector(`.mp-tag-input-search-input`);
    const span = document.createElement(`span`);
    span.className = `mp-tag-input-search-input-dummy`;
    span.innerText = searchInput.value;
    this.el.appendChild(span);
    const buffer = 24;
    this.update({inputWidth: span.offsetWidth + buffer + `px`});
    this.el.removeChild(span);
  }

  _removeTag(tag) {
    const tags = this.state.tags;
    tags.delete(tag);
    this.update({tags});
    this.dispatchEvent(new CustomEvent(`change`, {detail: {tag, action: `removeTag`}}));
    requestAnimationFrame(() => this.focus());
  }
}

export const TagInputEl = defineAndMakeJsxFactory<TagInputJsxProps>(`mp-tag-input`, TagInput);
